import React, { useState, useEffect } from 'react';
import { Upload, Button, Modal } from 'antd';
import { UploadOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';

interface ImageUploadButtonProps {
  onImageUpload?: (file: File) => void;
}

const ImageUploadButton: React.FC<ImageUploadButtonProps> = ({ onImageUpload }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleUpload: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileList(newFileList);

    if (newFileList.length > 0 && newFileList[0].originFileObj) {
      const file = newFileList[0].originFileObj as File;
      const url = URL.createObjectURL(file);
      setImageUrl(url);
      if (onImageUpload) {
        onImageUpload(file);
      }
    } else {
      setImageUrl(null);
    }
  };

  const handleRemoveImage = () => {
    setFileList([]);
    setImageUrl(null);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  // Main container style - now includes display: flex
  const containerStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    minHeight: '45px',
    display: 'flex', // Added this
  };

  // Upload wrapper style - crucial fix
  const uploadWrapperStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex', // Added this
  };

  // Custom upload button style
  const uploadButtonStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    minHeight: '45px',
    border: '1px dotted #BD273B',
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    cursor: 'pointer',
    position: 'relative',
    backgroundColor: imageUrl ? 'transparent' : '#d9d9d9',
    color: imageUrl ? 'transparent' : '#fff',
    flex: 1, // Added this to make it fill available space
  };

  // Container for icons
  const iconContainerStyle: React.CSSProperties = {
    position: 'absolute',
    display: 'flex',
    gap: '10px',
    padding: '8px',
    borderRadius: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
  };

  // Icon style
  const iconStyle: React.CSSProperties = {
    fontSize: '18px',
    color: '#000',
    cursor: 'pointer',
    transition: 'transform 0.2s',
  };

  // Props for the Upload component
  const uploadProps: UploadProps = {
    fileList,
    onChange: handleUpload,
    accept: 'image/*',
    showUploadList: false,
    beforeUpload: () => false,
    maxCount: 1,
    style: { // Added this to style the Upload component directly
      display: 'flex',
      width: '100%',
      height: '100%',
    },
  };

  return (
    <div style={containerStyle}>
      <div style={uploadWrapperStyle}>
        <Upload {...uploadProps}>
          <Button
            style={uploadButtonStyle}
            icon={<UploadOutlined />}
          >
            {imageUrl ? '' : 'Upload Image'}
            {imageUrl && (
              <div style={iconContainerStyle}>
                <EyeOutlined
                  style={iconStyle}
                  onClick={(e) => {
                    e.stopPropagation();
                    showModal();
                  }}
                />
                <DeleteOutlined
                  style={iconStyle}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage();
                  }}
                />
              </div>
            )}
          </Button>
        </Upload>
      </div>

      <Modal
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        centered
        bodyStyle={{ padding: 0 }}
      >
        {imageUrl && <img src={imageUrl} alt="Uploaded" style={{ width: '100%' }} />}
      </Modal>
    </div>
  );
};

export default ImageUploadButton;
